import {DocumentNode, gql} from '@apollo/client';
import {groupBy} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {collapseTypedValuesForMultiValuePlateMap} from 'procedures/planning/multiValuePlateMap';
import {
  ExperimentalInputs,
  ExperimentalInputTypedValuesByComponentName,
} from 'procedures/planning/ProcedurePlanTypes';
import {getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT,
  ExperimentFlowchart,
  ExperimentMethod,
  getOrderedTemplateMethods,
  prepareInitialExperimentalInputs,
} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_CONFIG_VERSION_FRAGMENT} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {
  getDefaultPlanConditionsForMethods,
  makeDefaultPlanCondition,
  resizeExperimentalInputsToPlanConditions,
  PlanConditionsByMethodId,
  withPlanConditionValidationStates,
} from 'studies/typed/shared/procedure-runs/design-state/planConditionState';
import {Condition} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {GetStudyDesignFromProcedureRunPlanGraphQL as ProcedureRunPlanNode} from './__generated/GetStudyDesignFromProcedureRunPlanGraphQL';

export interface StudyDesignParams {
  experimentalInputs: ExperimentalInputs;
  templateFlowchart: ExperimentFlowchart;
  planConditionsByMethodId: PlanConditionsByMethodId;
}

/**
 * Extracts the study design from a ProcedureRunPlanNode.
 *
 * @param procedureRunPlan - a ProcedureRunPlanNode that contains STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT
 * @returns experimental inputs, the template flowchart, and condition rows keyed by method.
 */
export default function getStudyDesignFromProcedureRunPlan(
  procedureRunPlan: ProcedureRunPlanNode
): StudyDesignParams {
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  assert(flowchartVersion != null, 'Expected flowchart version to be present');
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible');

  const procedureIsPoW = procedureRunPlan.procedureFlowchartConfigVersion != null;
  const templateMethods = getOrderedTemplateMethods(flowchartVersion, procedureIsPoW);

  const templateFlowchart: ExperimentFlowchart = {
    ...flowchartVersion,
    nodes: templateMethods,
  };

  const methodIds = templateMethods.map((method) => method.id);
  const rowsByMethodId = rowsByMethodSortedByPosition(procedureRunPlan.procedureRunPlanConditions);
  const planConditionsByMethodId = withPlanConditionValidationStates(
    procedureRunPlan.procedureRunPlanConditions.length === 0
      ? getDefaultPlanConditionsForMethods(methodIds)
      : planConditionsByMethodFromRows(rowsByMethodId, methodIds)
  );

  const confirmationMethodsByParamVersionId = new Map(
    procedureRunPlan.confirmationMethodByParamVersionId.map((cm) => [
      cm.paramVersionId,
      cm.confirmationMethod,
    ])
  );

  const prevConfirmationMethods = Object.fromEntries(
    templateMethods.map((methodNode) => [
      methodNode.id,
      {
        parameterConfirmationMethods: getConfirmationMethodsForMethod(
          methodNode,
          confirmationMethodsByParamVersionId
        ),
      },
    ])
  );

  // 0 conditions because `values` (one entry per condition) is rebuilt from the stored rows below.
  const preparedInputs = prepareInitialExperimentalInputs(0, templateMethods, {prevConfirmationMethods});
  const loadedInputs: ExperimentalInputs = Object.fromEntries(
    templateMethods.map((methodNode) => {
      const inputs = preparedInputs[methodNode.id];
      const rows = rowsByMethodId.get(methodNode.id) ?? [];
      return [
        methodNode.id,
        {
          ...inputs,
          values: rows.map((row) =>
            mergeValuesWithDefaults(
              getValuesFromMethodPlan(methodNode, row.methodParametersPlan),
              inputs.defaultValues
            )
          ),
        },
      ];
    })
  );
  // Each method needs one `values` entry per condition. A plan with no stored conditions gets a default
  // condition per method, but its `values` is empty. Resizing makes each method's `values` the same length
  // as `planConditionsByMethodId[methodId]`, filling new entries with defaults.
  const experimentalInputs = resizeExperimentalInputsToPlanConditions(planConditionsByMethodId, loadedInputs);

  return {
    experimentalInputs,
    templateFlowchart,
    planConditionsByMethodId,
  };
}

type MethodPlan = ProcedureRunPlanNode['procedureRunPlanConditions'][number]['methodParametersPlan'];

type StoredPlanCondition = ProcedureRunPlanNode['procedureRunPlanConditions'][number];

/**
 * One array per flowchart method, ordered by backend position. A method with no stored rows gets one
 * default unsaved row, so every method always has at least one condition.
 */
function planConditionsByMethodFromRows(
  rowsByMethodId: Map<string, Array<StoredPlanCondition>>,
  methodIds: ReadonlyArray<string>
): PlanConditionsByMethodId {
  return Object.fromEntries(
    methodIds.map((methodId) => {
      const storedRows = rowsByMethodId.get(methodId) ?? [];
      return [
        methodId,
        storedRows.length > 0
          ? storedRows.map((row) => storedRowToPlanCondition(row))
          : [makeDefaultPlanCondition()],
      ];
    })
  );
}

/**
 * Groups rows by method, ordered by backend `position`. A gap between stored positions collapses, so each
 * array is dense and its index is the row's position when saved back.
 *
 * Assumes a method's positions only order its own rows. They are not matched across methods, so a gap is
 * renumbered on the next save.
 */
function rowsByMethodSortedByPosition(
  storedConditions: Array<StoredPlanCondition>
): Map<string, Array<StoredPlanCondition>> {
  const conditionRowsByMethodId = new Map<string, Array<StoredPlanCondition>>();
  [...storedConditions]
    .sort((a, b) => a.position - b.position)
    .forEach((conditionRow) => {
      const methodId = getAccessibleOrInaccessibleId(conditionRow.templateMethodVersion);
      const conditionRows = conditionRowsByMethodId.get(methodId) ?? [];
      conditionRows.push(conditionRow);
      conditionRowsByMethodId.set(methodId, conditionRows);
    });
  return conditionRowsByMethodId;
}

function storedRowToPlanCondition(row: StoredPlanCondition): Condition {
  // An empty description is stored as '' or null depending on the writer; treat both as absent so
  // methods that agree are not mistaken for methods that disagree.
  const description = row.description || undefined;
  return {
    id: row.id,
    name: row.name,
    numReplicates: row.replicates.length > 0 ? row.replicates.length : 1,
    ...(description !== undefined ? {description} : {}),
  };
}

/**
 * Merges loaded values with defaults at the parameter level.
 * For each parameter, if the loaded value is an empty array (no stored value), use the default instead.
 * This keeps a parameter without a stored value showing the template default.
 */
function mergeValuesWithDefaults(
  loadedValues: ExperimentalInputTypedValuesByComponentName,
  defaultValues: ExperimentalInputTypedValuesByComponentName
): ExperimentalInputTypedValuesByComponentName {
  return {
    parameters: loadedValues.parameters.map((paramFields, paramIdx) =>
      paramFields.length > 0 ? paramFields : defaultValues.parameters[paramIdx]
    ),
    materialInputs: loadedValues.materialInputs.map((inputFields, inputIdx) =>
      inputFields.length > 0 ? inputFields : defaultValues.materialInputs[inputIdx]
    ),
    equipment: loadedValues.equipment.map((equipFields, equipIdx) =>
      equipFields.length > 0 ? equipFields : defaultValues.equipment[equipIdx]
    ),
  };
}

/**
 * Extracts parameter values from a method plan, matching them to the template method's parameter order.
 * Similar to getValuesForMethod but for plan stored values instead of PMEI planned values.
 *
 * Plan values are stored as JSONScalar with different shapes:
 * - Data parameters: { value: TypedValue }
 * - Material inputs: { value: { material: TypedValue, amount: TypedValue } }
 * - Equipment: { value: { equipment: TypedValue } }
 *
 * For plate-confirmed parameters with multiple setpoints, the plan may contain multiple stored
 * entries sharing a single `parameterId` / `materialInputId`. Those are collapsed back into a
 * single multi-value TypedValue (ARRAY or multi-link BIOENTITY_LINK) for the UI, matching the
 * shape `useInputTable` expects for `isMulti: true` columns.
 */
function getValuesFromMethodPlan(
  templateMethod: ExperimentMethod,
  methodPlan: MethodPlan
): ExperimentalInputTypedValuesByComponentName {
  if (methodPlan == null) {
    return {
      parameters: templateMethod.dataParameterSettings.map(() => []),
      materialInputs: templateMethod.materialInputSettings.map(() => []),
      equipment: templateMethod.equipmentSettings.map(() => []),
    };
  }

  const dataParamValuesById = groupBy(methodPlan.dataParameterValues, (r) => r.parameterId);
  const materialInputValuesById = groupBy(methodPlan.materialInputValues, (r) => r.materialInputId);
  const equipmentValuesById = groupBy(methodPlan.equipmentValues, (r) => r.equipmentId);

  return {
    parameters: templateMethod.dataParameterSettings.map((setting) => {
      const storedEntries = dataParamValuesById[setting.version.id] ?? [];
      const perEntryValues = storedEntries.map(
        (row) => (row.value as {value: TypedValue} | null)?.value ?? null
      );
      const collapsed = collapseTypedValuesForMultiValuePlateMap(perEntryValues);
      return collapsed != null ? [collapsed] : [];
    }),

    materialInputs: templateMethod.materialInputSettings.map((setting) => {
      const storedEntries = materialInputValuesById[setting.version.id] ?? [];
      const innerValues = storedEntries.map(
        (row) =>
          (row.value as {value: {material: TypedValue | null; amount: TypedValue | null}} | null)?.value
      );
      const perEntryMaterials = innerValues.map((iv) => iv?.material ?? null);
      const collapsedMaterial = collapseTypedValuesForMultiValuePlateMap(perEntryMaterials);
      const amount = innerValues.map((iv) => iv?.amount ?? null).find((v) => v != null) ?? null;
      if (collapsedMaterial == null && amount == null) {
        return [];
      }
      return [collapsedMaterial, amount] as Array<TypedValue>;
    }),

    equipment: templateMethod.equipmentSettings.map((setting) => {
      const storedEntries = equipmentValuesById[setting.version.id] ?? [];
      assert(
        storedEntries.length <= 1,
        `Expected at most one stored equipment entry for ${setting.version.id}, got ${storedEntries.length}`
      );
      const value =
        (storedEntries[0]?.value as {value: {equipment: TypedValue | null}} | null)?.value?.equipment ?? null;
      return value != null ? [value] : [];
    }),
  };
}

/**
 * Gets confirmation methods for a method's parameters based on the plan-level mapping.
 */
function getConfirmationMethodsForMethod(
  methodNode: ExperimentMethod,
  confirmationMethodsByParamVersionId: Map<string, ProcedureParameterConfirmationMethod>
): ExperimentalInputs[string]['parameterConfirmationMethods'] {
  return {
    parameters: methodNode.dataParameterSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
    materialInputs: methodNode.materialInputSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
    equipment: methodNode.equipmentSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
  };
}

export const STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT: DocumentNode = gql`
  fragment GetStudyDesignFromProcedureRunPlanGraphQL on ProcedureRunPlanNode {
    id
    description
    dataEntryMode
    templateProcedureFlowchartVersion {
      ... on ProcedureFlowchartVersionNode {
        id
        ...ExperimentalInputsFlowchartGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    procedureFlowchartConfigVersion {
      ...ExperimentalInputsProcedureFlowchartConfigVersionGraphQL
      ...InaccessibleInfoGraphQL
    }
    procedureRunPlanConditions {
      id
      name
      description
      position
      templateMethodVersion {
        ... on ProcedureMethodDefinitionVersionNode {
          id
        }
        ... on InaccessibleInfoNode {
          ...InaccessibleInfoGraphQL
        }
      }
      replicates {
        replicateNumber
      }
      methodParametersPlan {
        dataParameterValues {
          parameterId
          value
        }
        materialInputValues {
          materialInputId
          value
        }
        equipmentValues {
          equipmentId
          value
        }
      }
    }
    confirmationMethodByParamVersionId {
      paramVersionId
      confirmationMethod
    }
    conditionReplicateFlowchart {
      id
      sourceReplicate {
        replicateNumber
        procedureRunPlanCondition {
          id
        }
      }
      targetReplicate {
        replicateNumber
        procedureRunPlanCondition {
          id
        }
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT}
  ${EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_CONFIG_VERSION_FRAGMENT}
`;
